import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Clock,
  ArrowUp,
  ArrowDown,
  Layers,
  ChevronDown,
  ChevronRight,
  Copy,
  Truck,
  X,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

// ==================== Cargo Template Editor (P4) ====================
// Renders a small collapsible panel inside a transport-spawning Setup Profile
// step that lets the dispatcher declare what should ride on the auto-spawned
// trailer (asset types + attachment types). Stored as TYPE ids on the step;
// the actual instances are filled in by the dispatcher on the Planning Board.
const CargoTemplateEditor = ({ step, stepIndex, onChange, equipmentTypes, attachmentTypes }) => {
  const tpl = step.cargo_template || { asset_type_ids: [], attachment_type_ids: [] };
  const assetIds = tpl.asset_type_ids || [];
  const attachmentIds = tpl.attachment_type_ids || [];
  const hasAny = assetIds.length > 0 || attachmentIds.length > 0;
  const [expanded, setExpanded] = useState(hasAny);

  const update = (patch) => {
    onChange({
      asset_type_ids: patch.asset_type_ids !== undefined ? patch.asset_type_ids : assetIds,
      attachment_type_ids: patch.attachment_type_ids !== undefined ? patch.attachment_type_ids : attachmentIds,
    });
  };

  const addAssetType = (id) => {
    if (!id || assetIds.includes(id)) return;
    update({ asset_type_ids: [...assetIds, id] });
  };
  const removeAssetType = (id) => update({ asset_type_ids: assetIds.filter(x => x !== id) });
  const addAttachmentType = (id) => {
    if (!id || attachmentIds.includes(id)) return;
    update({ attachment_type_ids: [...attachmentIds, id] });
  };
  const removeAttachmentType = (id) => update({ attachment_type_ids: attachmentIds.filter(x => x !== id) });

  const assetTypesById = equipmentTypes.reduce((m, t) => { m[t.id] = t; return m; }, {});
  const attTypesById = (attachmentTypes || []).reduce((m, t) => { m[t.id] = t; return m; }, {});

  // Exclude the step's own asset type. Also exclude any asset that IS
  // designated as a prime mover for another asset type (e.g. Truck) —
  // pullers aren't cargo. Transport-spawning assets ARE selectable (e.g.
  // a small Franna could ride on a bigger Low-Loader).
  const primeMoverAssetTypeIds = new Set(
    equipmentTypes
      .filter(t => t.requires_prime_mover && t.prime_mover_asset_type_id)
      .map(t => t.prime_mover_asset_type_id)
  );
  const cargoableAssetTypes = equipmentTypes
    .filter(t =>
      t.is_active
      && t.name !== step.asset_type
      && !primeMoverAssetTypeIds.has(t.id)
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="mt-3 border-t pt-3" data-testid={`cargo-template-${stepIndex}`}>
      <button
        type="button"
        className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <Truck className="h-3 w-3" />
        <span>Load Template</span>
        {hasAny && (
          <Badge variant="outline" className="ml-1 h-5 px-1.5 text-[10px]">
            {assetIds.length + attachmentIds.length}
          </Badge>
        )}
        <span className="text-muted-foreground/70 font-normal ml-1">
          — what rides on this trailer
        </span>
      </button>

      {expanded && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 pl-5">
          {/* Asset types */}
          <div data-testid={`cargo-template-assets-${stepIndex}`}>
            <Label className="text-xs">Asset types on board</Label>
            <div className="mt-1 flex flex-wrap gap-1.5 min-h-[32px]">
              {assetIds.map(id => {
                const t = assetTypesById[id];
                return (
                  <Badge
                    key={id}
                    variant="secondary"
                    className="flex items-center gap-1 pr-1"
                    data-testid={`cargo-template-asset-chip-${id}`}
                  >
                    {t?.name || id}
                    <button
                      type="button"
                      onClick={() => removeAssetType(id)}
                      className="hover:text-destructive"
                      aria-label={`Remove ${t?.name || id}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                );
              })}
              {assetIds.length === 0 && (
                <span className="text-xs text-muted-foreground italic">None</span>
              )}
            </div>
            <Select value="" onValueChange={addAssetType}>
              <SelectTrigger className="mt-2 h-8 text-xs" data-testid={`cargo-template-asset-add-${stepIndex}`}>
                <SelectValue placeholder="+ Add asset type" />
              </SelectTrigger>
              <SelectContent>
                {cargoableAssetTypes
                  .filter(t => !assetIds.includes(t.id))
                  .map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Attachment types */}
          <div data-testid={`cargo-template-attachments-${stepIndex}`}>
            <Label className="text-xs">Attachment types on board</Label>
            <div className="mt-1 flex flex-wrap gap-1.5 min-h-[32px]">
              {attachmentIds.map(id => {
                const t = attTypesById[id];
                return (
                  <Badge
                    key={id}
                    variant="secondary"
                    className="flex items-center gap-1 pr-1"
                    data-testid={`cargo-template-attachment-chip-${id}`}
                  >
                    {t?.name || id}
                    <button
                      type="button"
                      onClick={() => removeAttachmentType(id)}
                      className="hover:text-destructive"
                      aria-label={`Remove ${t?.name || id}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                );
              })}
              {attachmentIds.length === 0 && (
                <span className="text-xs text-muted-foreground italic">None</span>
              )}
            </div>
            <Select value="" onValueChange={addAttachmentType}>
              <SelectTrigger className="mt-2 h-8 text-xs" data-testid={`cargo-template-attachment-add-${stepIndex}`}>
                <SelectValue placeholder="+ Add attachment type" />
              </SelectTrigger>
              <SelectContent>
                {(attachmentTypes || [])
                  .filter(t => !attachmentIds.includes(t.id))
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
};

const SetupProfilesTab = ({ api, equipmentTypes, attachmentTypes = [] }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [expandedProfile, setExpandedProfile] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningProfile, setAssigningProfile] = useState(null);
  const [assignedTypeIds, setAssignedTypeIds] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [nameError, setNameError] = useState('');

  // Form state
  const [form, setForm] = useState({
    name: '',
    description: '',
    is_active: true,
    steps: []
  });

  const activeTypeNames = equipmentTypes
    .filter(t => t.is_active)
    .map(t => t.name)
    .sort();

  // Lookup equipment type by name (used to detect transport-spawning steps)
  const equipmentTypeByName = equipmentTypes.reduce((map, t) => {
    map[t.name] = t;
    return map;
  }, {});

  // Does the step's asset carry cargo? Either it auto-spawns a transport
  // leg (Crane → Low-Loader) OR it pairs with a prime mover (Trailer → Truck).
  // In both cases the trailer-shaped asset can hold a declared load.
  const stepCanCarryCargo = (assetTypeName) => {
    const et = equipmentTypeByName[assetTypeName];
    if (!et) return false;
    const spawnsTransport = et.requires_transport && !et.requires_prime_mover && et.transport_equipment_type_id;
    return Boolean(spawnsTransport || et.requires_prime_mover);
  };

  const fetchProfiles = async () => {
    try {
      const response = await api.get('/setup-profiles');
      setProfiles(response.data);
    } catch (error) {
      console.error('Failed to fetch setup profiles', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProfiles(); }, [api]);

  const filteredProfiles = profiles.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const resetForm = () => {
    setForm({ name: '', description: '', is_active: true, steps: [] });
    setEditingProfile(null);
    setNameError('');
  };

  const handleOpenDialog = (profile = null) => {
    if (profile) {
      setEditingProfile(profile);
      setForm({
        name: profile.name,
        description: profile.description || '',
        is_active: profile.is_active,
        steps: (profile.steps || []).map(s => ({ ...s }))
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const addStep = () => {
    setForm(prev => ({
      ...prev,
      steps: [...prev.steps, {
        step_order: prev.steps.length + 1,
        asset_type: activeTypeNames[0] || '',
        label: '',
        quantity: 1,
        offset_minutes: 0,
        duration_minutes: null,
        dependencies: [],
        optional: false,
        worker_requirements: [],
        cargo_template: null,
      }]
    }));
  };

  const removeStep = (index) => {
    setForm(prev => {
      const newSteps = prev.steps.filter((_, i) => i !== index);
      return { ...prev, steps: newSteps.map((s, i) => ({ ...s, step_order: i + 1 })) };
    });
  };

  const updateStep = (index, field, value) => {
    setForm(prev => {
      const newSteps = [...prev.steps];
      newSteps[index] = { ...newSteps[index], [field]: value };
      return { ...prev, steps: newSteps };
    });
  };

  const moveStep = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= form.steps.length) return;
    setForm(prev => {
      const newSteps = [...prev.steps];
      [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
      return { ...prev, steps: newSteps.map((s, i) => ({ ...s, step_order: i + 1 })) };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Profile name is required');
      return;
    }
    // Duplicate-name check (case-insensitive, exclude self when editing)
    const trimmedName = form.name.trim();
    const duplicate = profiles.find(
      p => p.name.toLowerCase() === trimmedName.toLowerCase() && p.id !== editingProfile?.id
    );
    if (duplicate) {
      setNameError(`A setup profile named "${duplicate.name}" already exists`);
      return;
    }
    setNameError('');
    if (form.steps.length === 0) {
      toast.error('Add at least one step');
      return;
    }
    for (const s of form.steps) {
      if (!s.label.trim()) {
        toast.error('All steps must have a label');
        return;
      }
      if (!s.asset_type) {
        toast.error('All steps must have an asset type');
        return;
      }
    }

    try {
      if (editingProfile) {
        await api.put(`/setup-profiles/${editingProfile.id}`, form);
        toast.success('Setup profile updated');
      } else {
        await api.post('/setup-profiles', form);
        toast.success('Setup profile created');
      }
      setDialogOpen(false);
      resetForm();
      fetchProfiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = (profile) => {
    setDeleteTarget(profile);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/setup-profiles/${deleteTarget.id}`);
      toast.success('Setup profile deleted');
      fetchProfiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleDuplicate = async (profile) => {
    try {
      await api.post('/setup-profiles', {
        name: `${profile.name} (Copy)`,
        description: profile.description,
        is_active: true,
        steps: (profile.steps || []).map(s => ({
          step_order: s.step_order,
          asset_type: s.asset_type,
          label: s.label,
          quantity: s.quantity,
          offset_minutes: s.offset_minutes,
          duration_minutes: s.duration_minutes,
          dependencies: [],
          optional: s.optional,
          worker_requirements: s.worker_requirements || [],
          cargo_template: s.cargo_template || null,
        }))
      });
      toast.success('Profile duplicated');
      fetchProfiles();
    } catch (error) {
      toast.error('Duplicate failed');
    }
  };

  // Assignment dialog
  const openAssignDialog = (profile) => {
    setAssigningProfile(profile);
    const typesWithProfile = equipmentTypes.filter(
      t => (t.setup_profile_ids || []).includes(profile.id)
    );
    setAssignedTypeIds(typesWithProfile.map(t => t.id));
    setAssignDialogOpen(true);
  };

  const handleAssignSave = async () => {
    if (!assigningProfile) return;
    try {
      // For each equipment type, update its profile assignments
      for (const eqType of equipmentTypes) {
        const wasAssigned = (eqType.setup_profile_ids || []).includes(assigningProfile.id);
        const isNowAssigned = assignedTypeIds.includes(eqType.id);

        if (wasAssigned && !isNowAssigned) {
          await api.delete(`/equipment-types/${eqType.id}/setup-profiles/${assigningProfile.id}`);
        } else if (!wasAssigned && isNowAssigned) {
          const currentIds = eqType.setup_profile_ids || [];
          await api.post(`/equipment-types/${eqType.id}/setup-profiles`, {
            profile_ids: [...currentIds, assigningProfile.id],
            default_profile_id: currentIds.length === 0 ? assigningProfile.id : (eqType.default_setup_profile_id || null)
          });
        }
      }
      toast.success('Profile assignments updated');
      setAssignDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Assignment failed');
    }
  };

  const toggleTypeAssignment = (typeId) => {
    setAssignedTypeIds(prev =>
      prev.includes(typeId)
        ? prev.filter(id => id !== typeId)
        : [...prev, typeId]
    );
  };

  const formatOffset = (minutes) => {
    if (minutes === 0) return 'Same time';
    const h = Math.floor(Math.abs(minutes) / 60);
    const m = Math.abs(minutes) % 60;
    const sign = minutes > 0 ? '+' : '-';
    if (h > 0 && m > 0) return `${sign}${h}h ${m}m`;
    if (h > 0) return `${sign}${h}h`;
    return `${sign}${m}m`;
  };

  if (loading) return <div className="animate-pulse h-64 bg-muted rounded" />;

  return (
    <div className="space-y-4" data-testid="setup-profiles-tab">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5" />
                Setup Profiles
              </CardTitle>
              <CardDescription>
                Define reusable mobilisation templates for complex asset deployments
              </CardDescription>
            </div>
            <Button onClick={() => handleOpenDialog()} data-testid="create-profile-btn">
              <Plus className="h-4 w-4 mr-2" /> New Profile
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search profiles..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-profiles-input"
              />
            </div>
            <Badge variant="outline">{profiles.length} profile{profiles.length !== 1 ? 's' : ''}</Badge>
          </div>

          {filteredProfiles.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Layers className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-lg font-medium">No setup profiles yet</p>
              <p className="text-sm mt-1">Create a profile to define mobilisation sequences for complex assets</p>
              <Button className="mt-4" onClick={() => handleOpenDialog()}>
                <Plus className="h-4 w-4 mr-2" /> Create First Profile
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredProfiles.map(profile => {
                const isExpanded = expandedProfile === profile.id;
                const assignedTypes = equipmentTypes.filter(
                  t => (t.setup_profile_ids || []).includes(profile.id)
                );
                return (
                  <div
                    key={profile.id}
                    className="border rounded-lg overflow-hidden"
                    data-testid={`profile-card-${profile.id}`}
                  >
                    {/* Header row */}
                    <div
                      className="flex items-center gap-3 p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => setExpandedProfile(isExpanded ? null : profile.id)}
                    >
                      {isExpanded ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">{profile.name}</span>
                          {!profile.is_active && <Badge variant="secondary">Inactive</Badge>}
                        </div>
                        {profile.description && (
                          <p className="text-sm text-muted-foreground truncate mt-0.5">{profile.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant="outline">{(profile.steps || []).length} steps</Badge>
                        {assignedTypes.length > 0 && (
                          <Badge variant="default">{assignedTypes.length} asset type{assignedTypes.length > 1 ? 's' : ''}</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" onClick={() => openAssignDialog(profile)} title="Assign to asset types" data-testid={`assign-profile-${profile.id}`}>
                          <Layers className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDuplicate(profile)} title="Duplicate">
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(profile)} title="Edit" data-testid={`edit-profile-${profile.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(profile)} title="Delete" data-testid={`delete-profile-${profile.id}`}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>

                    {/* Expanded step list */}
                    {isExpanded && (
                      <div className="border-t bg-muted/30 p-4">
                        {assignedTypes.length > 0 && (
                          <div className="mb-3 flex flex-wrap gap-1">
                            <span className="text-xs text-muted-foreground mr-1">Assigned to:</span>
                            {assignedTypes.map(t => (
                              <Badge key={t.id} variant="secondary" className="text-xs">{t.name}</Badge>
                            ))}
                          </div>
                        )}
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-12">#</TableHead>
                              <TableHead>Label</TableHead>
                              <TableHead>Asset Type</TableHead>
                              <TableHead className="w-20">Qty</TableHead>
                              <TableHead className="w-28">Offset</TableHead>
                              <TableHead className="w-24">Load</TableHead>
                              <TableHead className="w-24">Optional</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(profile.steps || []).sort((a, b) => a.step_order - b.step_order).map(step => {
                              const tpl = step.cargo_template || {};
                              const cargoCount = (tpl.asset_type_ids || []).length + (tpl.attachment_type_ids || []).length;
                              return (
                                <TableRow key={step.id}>
                                  <TableCell className="font-mono text-muted-foreground">{step.step_order}</TableCell>
                                  <TableCell className="font-medium">{step.label}</TableCell>
                                  <TableCell>{step.asset_type}</TableCell>
                                  <TableCell>{step.quantity}</TableCell>
                                  <TableCell>
                                    <Badge variant="outline" className="font-mono">
                                      <Clock className="h-3 w-3 mr-1" />
                                      {formatOffset(step.offset_minutes)}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    {cargoCount > 0 ? (
                                      <Badge variant="default" className="gap-1" data-testid={`profile-step-cargo-${step.id}`}>
                                        <Truck className="h-3 w-3" />
                                        {cargoCount}
                                      </Badge>
                                    ) : (
                                      <span className="text-muted-foreground text-xs">—</span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {step.optional && <Badge variant="secondary">Optional</Badge>}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) { setDialogOpen(false); resetForm(); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProfile ? 'Edit Setup Profile' : 'Create Setup Profile'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Profile details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Profile Name <span className="text-red-500">*</span></Label>
                <Input
                  value={form.name}
                  onChange={e => { setForm(prev => ({ ...prev, name: e.target.value })); setNameError(''); }}
                  placeholder="e.g., 200t+ Crane Mobilisation"
                  data-testid="profile-name-input"
                />
                {nameError && <p className="text-destructive text-xs mt-1" data-testid="profile-name-error">{nameError}</p>}
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch
                  checked={form.is_active}
                  onCheckedChange={val => setForm(prev => ({ ...prev, is_active: val }))}
                />
                <Label>Active</Label>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of when to use this profile"
                rows={2}
                data-testid="profile-description-input"
              />
            </div>

            {/* Steps */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold">Steps ({form.steps.length})</Label>
                <Button type="button" variant="outline" size="sm" onClick={addStep} data-testid="add-step-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add Step
                </Button>
              </div>

              {form.steps.length === 0 && (
                <div className="text-center py-8 border border-dashed rounded-lg text-muted-foreground">
                  <p>No steps defined. Add steps to define the mobilisation sequence.</p>
                </div>
              )}

              <div className="space-y-3">
                {form.steps.map((step, index) => (
                  <div key={`step-${index}`} className="border rounded-lg p-4 bg-card" data-testid={`step-row-${index}`}>
                    <div className="flex items-start gap-3">
                      {/* Reorder */}
                      <div className="flex flex-col items-center gap-1 pt-1">
                        <span className="text-xs font-mono text-muted-foreground w-5 text-center">{index + 1}</span>
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveStep(index, -1)} disabled={index === 0}>
                          <ArrowUp className="h-3 w-3" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveStep(index, 1)} disabled={index === form.steps.length - 1}>
                          <ArrowDown className="h-3 w-3" />
                        </Button>
                      </div>

                      {/* Fields */}
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <div>
                          <Label className="text-xs">Label <span className="text-red-500">*</span></Label>
                          <Input
                            value={step.label}
                            onChange={e => updateStep(index, 'label', e.target.value)}
                            placeholder="e.g., Counterweight Truck 1"
                            data-testid={`step-label-${index}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Asset Type <span className="text-red-500">*</span></Label>
                          <Select value={step.asset_type} onValueChange={val => updateStep(index, 'asset_type', val)}>
                            <SelectTrigger data-testid={`step-asset-type-${index}`}>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              {activeTypeNames.map(name => (
                                <SelectItem key={name} value={name}>{name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Offset (minutes)</Label>
                          <Input
                            type="number"
                            min={0}
                            value={step.offset_minutes}
                            onChange={e => updateStep(index, 'offset_minutes', parseInt(e.target.value) || 0)}
                            data-testid={`step-offset-${index}`}
                          />
                        </div>
                        <div className="flex items-end gap-3">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={step.optional}
                              onCheckedChange={val => updateStep(index, 'optional', val)}
                            />
                            <Label className="text-xs">Optional</Label>
                          </div>
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeStep(index)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Load Template (P4) — on transport-spawning OR prime-mover steps */}
                    {stepCanCarryCargo(step.asset_type) && (
                      <CargoTemplateEditor
                        step={step}
                        stepIndex={index}
                        onChange={(tpl) => updateStep(index, 'cargo_template', tpl)}
                        equipmentTypes={equipmentTypes}
                        attachmentTypes={attachmentTypes}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
              <Button type="submit" data-testid="save-profile-btn">
                {editingProfile ? 'Update Profile' : 'Create Profile'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign to Asset Types Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assign to Asset Types</DialogTitle>
            {assigningProfile && (
              <p className="text-sm text-muted-foreground">
                Select which asset types should use "{assigningProfile.name}"
              </p>
            )}
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {equipmentTypes.filter(t => t.is_active).sort((a, b) => a.name.localeCompare(b.name)).map(eqType => (
              <label
                key={eqType.id}
                className="flex items-center gap-3 p-2 rounded hover:bg-muted cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={assignedTypeIds.includes(eqType.id)}
                  onChange={() => toggleTypeAssignment(eqType.id)}
                  className="rounded"
                />
                <span className="text-sm">{eqType.name}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAssignSave} data-testid="save-assignment-btn">Save Assignments</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-setup-profile-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              Delete Setup Profile
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the setup profile "{deleteTarget?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-setup-profile-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-setup-profile-button">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SetupProfilesTab;
